import { BaseAIClient } from './BaseAIClient';
import type { AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';
import { GrammarPromptBuilder } from '../grammar-core/GrammarPromptBuilder';
import { GrammarResponseParser } from '../grammar-core/GrammarResponseParser';
import { GOOGLE_RESPONSE_SCHEMA, ANALYSIS_TEMPERATURE } from '../grammar-core/GrammarConstants';

/**
 * Google Gemini API client implementation
 */
export class GoogleClient extends BaseAIClient {
  private get apiUrl(): string {
    // All Gemini 2.5 models need 'models/' prefix
    let modelPath = this.model;
    
    // Add 'models/' prefix if not already present
    if (!this.model.startsWith('models/')) {
      modelPath = `models/${this.model}`;
    }
    
    return `https://generativelanguage.googleapis.com/v1beta/${modelPath}:generateContent?key=${this.apiKey}`;
  }

  protected getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json'
    };
  }

  async sendMessage(prompt: string): Promise<string> {
    // For Pro model, try Flash as fallback due to thinking mode issues
    if (this.model.includes('pro')) {
      console.warn('[GoogleClient] Pro model has thinking mode issues, using Flash as fallback');
      // Temporarily use Flash model as fallback
      const flashModel = this.model.replace('pro', 'flash');
      const originalModel = this.model;
      this.model = flashModel;
      
      try {
        const result = await this.sendMessageInternal(prompt);
        this.model = originalModel; // Restore original model
        return result;
      } catch (error) {
        this.model = originalModel; // Restore original model
        throw error;
      }
    }
    
    return this.sendMessageInternal(prompt);
  }
  
  private async sendMessageInternal(prompt: string, expectsJson?: boolean): Promise<string> {
    if (expectsJson === undefined) {
      expectsJson = prompt.toLowerCase().includes('json');
    }
    const generationConfig: any = {
      maxOutputTokens: this.maxTokens,
      temperature: 0.3
    };

    // Configure thinking for different models
    if (this.model.includes('flash')) {
      // Flash models: disable thinking with budget 0
      generationConfig.thinkingConfig = {
        thinkingBudget: 0
      };
    } else if (this.model.includes('pro')) {
      // Pro models need more tokens for thinking  
      generationConfig.maxOutputTokens = Math.max(this.maxTokens, 15000);
    }
    
    // Add JSON response format if needed
    if (expectsJson) {
      generationConfig.responseMimeType = 'application/json';
    }
    
    const body = {
      contents: [{
        parts: [{ text: prompt }]
      }],
      generationConfig
    };

    if (this.isElectron()) {
      // Use Electron IPC for API requests
      const result = await window.api!.aiRequest({
        provider: 'google',
        url: this.apiUrl,
        headers: this.getHeaders(),
        body
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      // Check if response has the expected structure
      if (!result.data || !result.data.candidates || !result.data.candidates[0]) {
        console.error('[GoogleClient] Unexpected Electron response structure:', result.data);
        throw new Error('Invalid response from Google API via Electron');
      }

      // Handle Pro model responses that might have empty content
      const candidate = result.data.candidates[0];
      if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
        console.error('[GoogleClient] Empty response from model:', this.model);
        return '';  // Return empty string instead of throwing
      }

      if (!candidate.content.parts[0].text) {
        console.error('[GoogleClient] No text in response part');
        return '';
      }

      return candidate.content.parts[0].text;
    } else {
      // Direct fetch for development environment
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Google API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Check if response has the expected structure
      if (!data.candidates || !data.candidates[0]) {
        console.error('[GoogleClient] Unexpected response structure:', data);
        throw new Error('Invalid response from Google API');
      }
      
      // Handle Pro model responses that might have empty content
      const candidate = data.candidates[0];
      if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
        console.error('[GoogleClient] Empty response from model:', this.model);
        
        // For Pro models, the actual content might be after thinking
        // Try to return a default JSON response if expecting JSON
        if (expectsJson) {
          return JSON.stringify({ errors: [] });
        }
        return '';  // Return empty string for Pro models with thinking
      }
      
      if (!candidate.content.parts[0].text) {
        console.error('[GoogleClient] No text in response part:', candidate.content.parts[0]);
        return '';
      }
      
      return candidate.content.parts[0].text;
    }
  }


  async analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    const startTime = Date.now();

    try {
      // If no corrections to analyze, return empty array
      if (!request.corrections || request.corrections.length === 0) {
        return [];
      }

      // For large correction sets, process in batches to avoid timeout and response truncation
      const BATCH_SIZE = 5; // Process 5 corrections at a time for fastest first response
      const totalCorrections = request.corrections.length;

      if (totalCorrections > BATCH_SIZE) {
        console.log(`[Google] Processing ${totalCorrections} corrections in batches of ${BATCH_SIZE}`);

        const results: AIAnalysisResult[] = [];
        const batches = Math.ceil(totalCorrections / BATCH_SIZE);

        for (let i = 0; i < batches; i++) {
          const start = i * BATCH_SIZE;
          const end = Math.min(start + BATCH_SIZE, totalCorrections);
          const batchCorrections = request.corrections.slice(start, end);

          console.log(`[Google] Processing batch ${i + 1}/${batches} (corrections ${start + 1}-${end})`);

          const batchRequest = {
            ...request,
            corrections: batchCorrections
          };

          const batchResults = await this.processCorrectionBatch(batchRequest, start);
          results.push(...batchResults);

          // Call progress callback after each batch completes
          if (request.onProgress) {
            request.onProgress(results, {
              current: end,
              total: totalCorrections,
              batchIndex: i + 1,
              totalBatches: batches
            });
          }

          // Add delay between batches to respect server rate limits
          // Server has minute limit set to 9999 (effectively unlimited)
          // Use 100ms delay for fastest response (actual interval = API time ~1-2s + delay 0.1s)
          if (i < batches - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        const responseTime = Date.now() - startTime;
        console.log(`[Google] Completed ${totalCorrections} corrections in ${batches} batches (${responseTime}ms)`);

        return results;
      }

      // For small correction sets, process normally
      return await this.processCorrectionBatch(request, 0);

    } catch (error) {
      console.error('[Google] Analysis failed:', error);
      throw error;
    }
  }

  private async processCorrectionBatch(request: AIAnalysisRequest, indexOffset: number): Promise<AIAnalysisResult[]> {
    const correctionCount = request.corrections.length;

    // Define the response schema for structured output (both Pro and Flash support it)
    const responseSchema = {
      type: "array",
      items: {
        type: "object",
        properties: {
          correctionIndex: { type: "number" },
          selectedValue: { type: "string" },
          isExceptionProcessed: { type: "boolean" },
          keepOriginal: { type: "boolean" },
          reasoning: { type: "string" }
        },
        required: ["correctionIndex", "selectedValue", "isExceptionProcessed", "keepOriginal", "reasoning"]
      }
    };

    // Calculate max_tokens based on correction count
    // Each correction needs approximately 120 tokens for response
    const tokensForCorrections = correctionCount * 120;
    const baseTokens = 800; // Base overhead for JSON structure
    let maxOutputTokens = Math.max(this.maxTokens, baseTokens + tokensForCorrections);

    // Build generation config based on model
    const generationConfig: any = {
      temperature: 0.1,
      maxOutputTokens: maxOutputTokens,
      responseMimeType: "application/json",
      responseSchema: responseSchema
    };

    // Configure thinking for different models
    if (this.model.includes('flash')) {
      // Flash models: disable thinking with budget 0
      generationConfig.thinkingConfig = { thinkingBudget: 0 };
      // Cap at model-specific limits for Flash
      generationConfig.maxOutputTokens = Math.min(maxOutputTokens, 4000);
    } else if (this.model.includes('pro')) {
      // Pro models have thinking enabled by default
      // Need sufficient tokens (15000+) to include both thinking and actual response
      generationConfig.maxOutputTokens = Math.max(maxOutputTokens, 15000);
    } else {
      // Cap at model-specific limits for other models
      generationConfig.maxOutputTokens = Math.min(maxOutputTokens, 4000);
    }

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: GrammarPromptBuilder.buildGooglePrompt(request, 'ko')
          }]
        }],
        generationConfig
      })
    });

    if (!response.ok) {
      throw new Error(`Google API error: ${response.status}`);
    }

    const data = await response.json();

    // Improved Google API response handling
    if (!data.candidates || data.candidates.length === 0) {
      throw new Error('Google API returned no valid response');
    }

    const candidate = data.candidates[0];

    // Check if response has content (Pro model with thinking might have empty parts initially)
    if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
      console.warn('[Google] Response has no content parts, model:', this.model);

      // For Pro model with thinking, response might be in a different format
      if (this.model.includes('pro')) {
        console.error('[Google] Gemini Pro returned empty response. May need streaming API.');
      }
      throw new Error('Google API response has no content');
    }

    // Check if the text content exists
    if (!candidate.content.parts[0].text) {
      console.error('[Google] Response part has no text:', candidate.content.parts[0]);
      throw new Error('Google API response has no text content');
    }

    // With structured output, the response should already be valid JSON
    let parsedResult: AIAnalysisResult[];
    try {
      const responseText = candidate.content.parts[0].text;

      parsedResult = JSON.parse(responseText);

      // Validate the response structure
      if (!Array.isArray(parsedResult)) {
        console.error('[Google] Response is not an array:', parsedResult);
        throw new Error('Invalid response format');
      }

      // Ensure all required fields are present
      parsedResult = parsedResult.map((item: any) => ({
        correctionIndex: item.correctionIndex ?? 0,
        selectedValue: item.selectedValue || '',
        isExceptionProcessed: item.isExceptionProcessed ?? false,
        keepOriginal: item.keepOriginal ?? false,
        reasoning: item.reasoning || ''
      }));
    } catch (error) {
      console.error('[Google] Failed to parse structured response:', error);
      // Fallback to legacy parsing if structured output fails
      parsedResult = GrammarResponseParser.parseResponse(candidate.content.parts[0].text, 'Google');
    }

    // Adjust correctionIndex to account for batch offset
    if (indexOffset > 0) {
      parsedResult.forEach(result => {
        result.correctionIndex += indexOffset;
      });
    }

    return parsedResult;
  }

  isAvailable(): boolean {
    return !!this.apiKey;
  }

  getModelName(): string {
    return this.model;
  }

}